import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Car,
  CarFront,
  CheckCircle2,
  Cog,
  Copy,
  FileJson,
  FileText,
  Info,
  ListCheck,
  Loader,
  Loader2,
  MapPin,
  Phone,
  Settings,
  ShieldCheck,
  User,
} from "lucide-react";

import { isAlphanumeric } from "validator";
import { useService } from "../../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import { fetchProfile } from "../../../../Services/profile/profileservice";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import { getRCPremiumV4 } from "../../../../Services/ApiTestService/apiTestService";

function RcPremiumV4() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [vehicleNo, setVehicleNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleRcPremiumV4 = async () => {
    if (!profileData) return;

    const validateVehicleNum = isAlpha(vehicleNo);

    if (!vehicleNo) {
      return setInputError("Vehicle Number is Required");
    }

    if (vehicleNo && !validateVehicleNum) {
      return setInputError(
        "⚠️ Please enter alpha numeric in Vehicle No. field",
      );
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getRCPremiumV4({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_number: vehicleNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using Vehicle number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6  gap-6 flex justify-center items-center">
            {/* Vehicle Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Vehicle Number
              </label>

              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="Eg: GJ0***1256"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !vehicleNo}
                onClick={handleRcPremiumV4}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Vehicle
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-2 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                RC Details
              </h2>

              {response?.status?.code === 200 && (
                <RCDetailsDashboard response={response} />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}


export default RcPremiumV4;



const RCDetailsDashboard = ({ response }) => {
  // Safe exit if data is missing or status is invalid
  if (response?.status?.code !== 200 || !response?.data?.rc_details) {
    return null;
  }

  // Target the nested 'rc_details' object and registered_mobile
  const data = response.data.rc_details;
  const registeredMobile = response.data.registered_mobile?.mobile;

  // Helper to safely parse dates in "DD-MM-YYYY" format
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      // Reformat DD-MM-YYYY to YYYY-MM-DD for standard JS Date parsing
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(dateStr);
  };

  // Helper to check if a document is expired
  const isExpired = (dateString) => {
    const expiry = parseDate(dateString);
    if (!expiry || isNaN(expiry.getTime())) return false;
    return expiry < new Date();
  };

  // Reusable component for small data blocks
  const InfoBlock = ({ label, value, highlight = false, hasError = false }) => (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span
          className={`text-sm ${
            hasError
              ? "font-bold text-red-600"
              : highlight
              ? "font-bold text-gray-900"
              : "font-semibold text-gray-800"
          }`}
        >
          {value || "N/A"}
        </span>
        {hasError && <AlertTriangle size={14} className="text-red-500" />}
      </div>
    </div>
  );

  const safeLower = (str) => (str ? str.toLowerCase() : "");
  const isActiveStatus = data.status?.toUpperCase() === "ACTIVE";
  const ownerCountNum = parseInt(data.owner_count, 10) || 1;

  return (
    <div className="max-w-6xl mx-auto space-y-6 font-sans">
      {/* 1. Primary Hero Banner */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-100 flex flex-col items-start  justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl shadow-inner">
              <Car size={32} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {data.reg_no}
                </h2>
                <span
                  className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 uppercase tracking-wide ${
                    isActiveStatus
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {isActiveStatus ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    <AlertCircle size={14} />
                  )}
                  {data.status}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1 capitalize">
                {safeLower(data.vehicle_manufacturer_name)} •{" "}
                {safeLower(data.model)} • {data.type}
              </p>
            </div>
          </div>

          <div className="text-left border-t md:border-t-0 pt-4 md:pt-0">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              Registered Owner (
              {ownerCountNum === 1
                ? "1st Owner"
                : ownerCountNum === 2
                ? "2nd Owner"
                : `${ownerCountNum}th Owner`}
              )
            </p>
            <p className="text-lg font-bold text-gray-900 capitalize">
              {safeLower(data.owner_name)}
            </p>
            {data.owner_father_name && (
              <p className="text-xs text-gray-500 capitalize">
                C/O: {safeLower(data.owner_father_name)}
              </p>
            )}
            {registeredMobile && (
              <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                <Phone size={12} /> {registeredMobile}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 2. Detailed Data Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Registration & RTO Details */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <MapPin size={18} className="text-blue-500" />
            <h3 className="text-base font-bold text-gray-800">
              Registration Details
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <InfoBlock label="Authority (RTO)" value={data.reg_authority} />
            <InfoBlock label="Vehicle Class" value={data.class} />
            <InfoBlock label="Registration Date" value={data.reg_date} />
            <InfoBlock
              label="RC Expiry"
              value={data.rc_expiry_date}
              highlight
              hasError={isExpired(data.rc_expiry_date)}
            />
            <div className="col-span-2">
              <InfoBlock label="Address" value={data.present_address} />
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Settings size={18} className="text-gray-500" />
            <h3 className="text-base font-bold text-gray-800">
              Vehicle Specifications
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <div className="col-span-2">
              <InfoBlock label="Chassis Number" value={data.chassis} />
            </div>
            <div className="col-span-2">
              <InfoBlock label="Engine Number" value={data.engine} />
            </div>
            <InfoBlock label="Color" value={data.vehicle_colour} />
            <InfoBlock label="Body Type" value={data.body_type} />
            <InfoBlock label="Norms" value={data.norms_type} />
            <InfoBlock
              label="Engine Cap (CC)"
              value={data.vehicle_cubic_capacity ? `${data.vehicle_cubic_capacity} cc` : null}
            />
          </div>
        </div>

        {/* Compliance & Insurance */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <ShieldCheck size={18} className="text-green-500" />
            <h3 className="text-base font-bold text-gray-800">
              Compliance & Insurance
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-5 gap-x-6">
            <div>
              <InfoBlock
                label="Insurance Company"
                value={data.vehicle_insurance_company_name}
              />
              <p className="text-xs text-gray-500 mt-1 font-mono">
                {data.vehicle_insurance_policy_number || "No Policy Number"}
              </p>
            </div>
            <InfoBlock
              label="Insurance Valid Upto"
              value={data.vehicle_insurance_upto}
              highlight
              hasError={isExpired(data.vehicle_insurance_upto)}
            />

            <InfoBlock label="PUCC Number" value={data.pucc_number} />
            <InfoBlock
              label="PUCC Valid Upto"
              value={data.pucc_upto}
              highlight
              hasError={isExpired(data.pucc_upto)}
            />

            <InfoBlock
              label="Road Tax Paid Upto"
              value={data.vehicle_tax_upto}
              highlight
              hasError={isExpired(data.vehicle_tax_upto)}
            />
          </div>
        </div>

        {/* Finance & Weight */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <FileText size={18} className="text-purple-500" />
            <h3 className="text-base font-bold text-gray-800">
              Finance & Weight
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <div className="col-span-2">
              <InfoBlock
                label="Financer"
                value={data.rc_financer || "None (Cash/Fully Paid)"}
              />
            </div>

            <InfoBlock
              label="Commercial Vehicle"
              value={data.is_commercial ? "Yes" : "No"}
            />
            <InfoBlock
              label="Permit Details"
              value={data.permit_type || "Not Applicable"}
            />

            <InfoBlock
              label="Gross Weight"
              value={data.gross_vehicle_weight ? `${data.gross_vehicle_weight} kg` : null}
            />
            <InfoBlock
              label="Unladen Weight"
              value={data.unladen_weight ? `${data.unladen_weight} kg` : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

